'use client';
import './decode.css';
import { useEffect, useMemo, useState } from 'react';
import { DecodeGuessInput } from './DecodeGuessInput';
import { DecodeBoard } from './DecodeBoard';
import { DecodeReveal } from './DecodeReveal';
import { DecodeShareButton } from './DecodeShareButton';
import { DecodeDistribution } from './DecodeDistribution';
import { KeepPlayingFooter } from '../../_components/KeepPlayingFooter';
import { DailySlateNav } from '../../_components/DailySlateNav';
import { decodeAnswer, isSolved, scoreGuess, shareGrid } from '../lib/select';
import { MAX_GUESSES, type BoardResponse, type CanonGuess } from '../lib/types';

// Per-player session key (parity with SPOT's spot_session_key / Connections'
// cx_session_key). The results table is UNIQUE(puzzle_id, session_key); this
// MUST vary per player or every result collides onto one row. Persisted so a
// reload reuses the same key.
function getSessionKey(): string {
  if (typeof window === 'undefined') return 'ssr';
  let k = window.localStorage.getItem('decode_session_key');
  if (!k) {
    k =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Math.random()).slice(2);
    window.localStorage.setItem('decode_session_key', k);
  }
  return k;
}

export function DecodeGame({
  board,
  canon,
  userId,
}: {
  board: BoardResponse;
  canon: CanonGuess[];
  userId?: string;
}) {
  const answer = useMemo(
    () => decodeAnswer(board.puzzle.answer),
    [board.puzzle.answer],
  );

  // Restore a finished play (signed-in revisit): guesses_log holds the guessed
  // car ids, so we rehydrate the board from the canon and re-show the reveal
  // rather than letting the player replay. Lazy initializers run once.
  const canonById = useMemo(
    () => new Map(canon.map((e) => [e.id, e])),
    [canon],
  );
  const [guesses, setGuesses] = useState<CanonGuess[]>(() =>
    board.result
      ? board.result.guessesLog
          .map((id) => canonById.get(id))
          .filter((e): e is CanonGuess => !!e)
      : [],
  );
  const [done, setDone] = useState<null | { solved: boolean }>(() =>
    board.result ? { solved: board.result.solved } : null,
  );
  // Streak comes back from POST /decode/result; only known for a fresh finish
  // (a revisit rehydrates from board.result and leaves this null).
  const [streak, setStreak] = useState<number | null>(null);

  // Rehydrate a finished play for ANONYMOUS players on revisit. Signed-in
  // players get board.result from the server; anon completions live only in
  // localStorage (the server keys results by user_id, which an anon doesn't
  // have). Mirrors ConnectionsBoard's cache. Runs post-mount (no localStorage
  // during SSR); guarded so it never clobbers a server result or live play.
  useEffect(() => {
    if (board.result || done || guesses.length > 0) return;
    try {
      const raw = window.localStorage.getItem(
        `decode_result_${board.puzzle.id}`,
      );
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        solved: boolean;
        guessesLog: string[];
      };
      const restored = saved.guessesLog
        .map((id) => canonById.get(id))
        .filter((e): e is CanonGuess => !!e);
      setGuesses(restored);
      setDone({ solved: saved.solved });
    } catch {
      /* corrupt/unavailable cache — play fresh */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.result, board.puzzle.id, canonById]);

  const guessedIds = useMemo(
    () => new Set(guesses.map((g) => g.id)),
    [guesses],
  );

  const rows = guesses.map((g) => ({
    label: g.label,
    specs: g.specs,
    feedback: scoreGuess(g.specs, answer.specs),
  }));

  async function postResult(
    solved: boolean,
    guessed: CanonGuess[],
  ): Promise<void> {
    // Cache locally first so an anonymous revisit re-shows the reveal even if
    // the network POST fails (the game already played out client-side).
    try {
      window.localStorage.setItem(
        `decode_result_${board.puzzle.id}`,
        JSON.stringify({ solved, guessesLog: guessed.map((g) => g.id) }),
      );
    } catch {
      /* private mode / quota — non-fatal */
    }
    try {
      const res = await fetch('/api/spot/decode/result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-Id': userId } : {}),
        },
        body: JSON.stringify({
          puzzleId: board.puzzle.id,
          sessionKey: getSessionKey(),
          solved,
          guesses: guessed.length,
          guessesLog: guessed.map((g) => g.id),
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as { streak?: number };
        if (typeof data.streak === 'number') setStreak(data.streak);
      }
    } catch {
      /* best-effort */
    }
  }

  function onGuess(entry: CanonGuess) {
    if (done) return;
    const next = [...guesses, entry];
    setGuesses(next);
    if (isSolved(entry.id, answer.id)) {
      setDone({ solved: true });
      void postResult(true, next);
    } else if (next.length >= MAX_GUESSES) {
      setDone({ solved: false });
      void postResult(false, next);
    }
  }

  const finalGrid = done
    ? shareGrid(
        guesses.map((g) => scoreGuess(g.specs, answer.specs)),
        board.puzzle.number,
        done.solved ? guesses.length : 'X',
      )
    : '';

  // Coaching hint ladder — the Grid's rotating-hint pattern, keyed to how
  // deep into the guess budget the player is.
  const hints = [
    "Start wide — a '90s Japanese turbo rules out half the canon.",
    'Amber means close — bracket it.',
    "A green board isn't the win — the name is.",
  ];
  const hint = hints[Math.min(2, Math.floor(guesses.length / 3))];

  return (
    <main className="dc-stage">
      <header className="dc-head">
        <div className="dc-kicker">daily decode · thu &amp; sun</div>
        <h1 className="dc-title">
          De<em>code</em>
        </h1>
        <div className="dc-banner">
          Puzzle №{String(board.puzzle.number).padStart(3, '0')} · 8 specs ·{' '}
          {MAX_GUESSES} guesses
        </div>
        {/* Rules block — the Grid's three-beat pattern (goal / mechanic /
            strategy). The color legend lives INSIDE the mechanic line, so the
            heat scale is explained exactly once. */}
        {!done && (
          <div className="dc-rules">
            <span>
              <b>Name the mystery car in {MAX_GUESSES} guesses.</b> Any car is a
              guess.
            </span>
            <span>
              <b>Every guess grades 8 specs:</b> 🟩 exact · 🟨 close · 🟥 off —
              ▲▼ the answer sits higher / lower.
            </span>
            <span>
              Wrong cars aren’t wasted — each one narrows era, layout, power…
            </span>
          </div>
        )}
      </header>
      {/* Layout key — decodes the FF/FR/MR/RR/F4/M4/R4 codes in the board's
          "Layout" column. Collapsed by default: reference material, not rules —
          seven codes open was the bulkiest block on the page. */}
      <details className="dc-key">
        <summary>Layout codes — FF · FR · MR · RR · F4 · M4 · R4</summary>
        <div className="dc-key-list">
          {[
            ['FF', 'Front-engine, FWD'],
            ['FR', 'Front-engine, RWD'],
            ['MR', 'Mid-engine, RWD'],
            ['RR', 'Rear-engine, RWD'],
            ['F4', 'Front-engine, AWD'],
            ['M4', 'Mid-engine, AWD'],
            ['R4', 'Rear-engine, AWD'],
          ].map(([code, def]) => (
            <span key={code}>
              <strong>{code}</strong> <span className="dc-key-def">{def}</span>
            </span>
          ))}
        </div>
      </details>
      {!done && (
        <div className="dc-status">
          <div>
            Guess{' '}
            <span className="dc-num">
              {Math.min(guesses.length + 1, MAX_GUESSES)}
            </span>
            /{MAX_GUESSES}
          </div>
          <div className="dc-hint">{hint}</div>
        </div>
      )}
      <DecodeBoard rows={rows} />
      {/* Guess input sits BELOW the board so its autocomplete dropdown expands
          into empty space instead of covering the prior guess rows. */}
      {!done && (
        <DecodeGuessInput
          canon={canon}
          guessedIds={guessedIds}
          disabled={!!done}
          onGuess={onGuess}
        />
      )}
      {done && (
        <div style={{ textAlign: 'center' }}>
          <DecodeReveal
            answer={answer}
            solved={done.solved}
            guesses={guesses.length}
          />
          {streak != null && streak >= 2 && (
            <p style={{ fontWeight: 700, color: 'var(--apex-deep, #b3433a)' }}>
              🔥 {streak}-day streak
            </p>
          )}
          <pre style={{ fontFamily: 'monospace', lineHeight: 1.4 }}>
            {finalGrid}
          </pre>
          <DecodeShareButton
            grid={finalGrid}
            date={board.date}
            streak={streak ?? undefined}
          />
          <DecodeDistribution
            date={board.date}
            yourGuesses={guesses.length}
            solved={done.solved}
          />
          <KeepPlayingFooter
            game="decode"
            date={board.date}
            userId={userId}
            archiveHref="/games?game=decode"
            includeSelf
          />
        </div>
      )}
      <DailySlateNav currentGame="decode" date={board.date} userId={userId} />
    </main>
  );
}
